import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Check,
  Copy,
  Globe,
  KeyRound,
  Link2,
  RefreshCw,
  Search,
  Shield,
  Trash2,
  User as UserIcon,
  UserPlus,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, inviteUrl, isLoopback, reachableBases } from "@/api/client";
import { generatePassword, signInDetails } from "@/lib/password";
import AppFormModal from "@/components/AppFormModal";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";

const TABS = [
  { id: "users", label: "People" },
  { id: "apps", label: "Apps" },
];

function Stat({ label, value }) {
  return (
    <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
      <p className="text-white/40 text-xs">{label}</p>
      <p className="text-3xl font-bold mt-1">{value}</p>
    </div>
  );
}

function Pill({ tone = "neutral", children }) {
  const tones = {
    neutral: "bg-white/10 text-white/60 border-white/20",
    purple: "bg-purple-600/30 text-purple-200 border-purple-500/30",
    green: "bg-green-600/20 text-green-300 border-green-500/30",
    yellow: "bg-yellow-600/20 text-yellow-200 border-yellow-500/30",
    red: "bg-red-600/20 text-red-300 border-red-500/30",
  };
  return (
    <span className={`inline-flex items-center gap-1 border rounded-full px-2 py-0.5 text-xs ${tones[tone]}`}>
      {children}
    </span>
  );
}

/** Shows a generated invite link with a copy button — no mail server involved. */
function InviteLinkDialog({ invite, bases, onClose }) {
  const [copied, setCopied] = useState(false);
  // bases is ordered with LAN-reachable addresses first, so the default link is
  // one the recipient can actually open from their own PC.
  const [base, setBase] = useState(bases[0] ?? window.location.origin);

  if (!invite) return null;

  const link = inviteUrl(invite.token, base);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — select the link and copy it manually.");
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Invite link for {invite.email}</DialogTitle>
          <DialogDescription>
            Send this to them however you like. It works once, and expires in {invite.expiresInDays} days.
          </DialogDescription>
        </DialogHeader>

        {bases.length > 1 && (
          <div>
            <Label htmlFor="invite-base">Address they should use</Label>
            <select
              id="invite-base"
              value={base}
              onChange={(e) => setBase(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
            >
              {bases.map((b) => (
                <option key={b} value={b}>
                  {b}
                  {isLoopback(b) ? "  (this computer only)" : ""}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex gap-2">
          <Input readOnly value={link} onFocus={(e) => e.target.select()} className="text-xs" />
          <Button onClick={copy} size="icon" variant="outline" aria-label="Copy invite link">
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>

        {isLoopback(base) && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">
            This link only opens on the computer running App Hub. Pick another address above before
            sending it to someone else.
          </p>
        )}

        <Button onClick={onClose} className="w-full">Done</Button>
      </DialogContent>
    </Dialog>
  );
}

/** The details an admin passes on after creating an account or resetting one. */
function CredentialsDialog({ details, address, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!details) return null;

  const block = signInDetails({ address, email: details.email, password: details.password });

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(block);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — select the text and copy it manually.");
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Sign-in details for {details.email}</DialogTitle>
          <DialogDescription>
            This password is not stored anywhere readable, so copy it now — it can't be shown again.
            You can always set a new one.
          </DialogDescription>
        </DialogHeader>

        <pre className="overflow-x-auto whitespace-pre rounded-lg bg-gray-100 p-3 font-mono text-xs text-gray-800">
          {block}
        </pre>

        <Button onClick={copy} variant="outline" className="w-full">
          {copied ? <Check className="mr-2 h-4 w-4 text-green-600" /> : <Copy className="mr-2 h-4 w-4" />}
          {copied ? "Copied" : "Copy sign-in details"}
        </Button>

        <Button onClick={onClose} className="w-full">Done</Button>
      </DialogContent>
    </Dialog>
  );
}

function SetPasswordDialog({ person, address, onClose, onDone }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    // Offer a fresh suggestion each time the dialog opens.
    setPassword(person ? generatePassword() : "");
    setResult(null);
  }, [person]);

  if (!person) return null;
  if (result) {
    return <CredentialsDialog details={result} address={address} onClose={onClose} />;
  }

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.admin.setPassword(person.id, password);
      await onDone();

      if (res.signed_self_out) {
        toast.success("Your password was changed — signing you back in");
        window.location.reload();
        return;
      }
      setResult({ email: person.email, password });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Set password for {person.email}</DialogTitle>
          <DialogDescription>
            Replaces whatever they had and signs them out everywhere.
          </DialogDescription>
        </DialogHeader>

        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="new-password" className="mb-0">New password</Label>
            <button
              type="button"
              onClick={() => setPassword(generatePassword())}
              className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
            >
              <RefreshCw className="h-3 w-3" /> Generate
            </button>
          </div>
          <Input
            id="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 font-mono text-sm"
            spellCheck={false}
            autoFocus
          />
          <p className="mt-1 text-xs text-gray-500">At least 8 characters.</p>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={save} disabled={busy || password.length < 8} className="flex-1">
            {busy ? "Saving…" : "Set password"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const { user: me } = useAuth();
  const [tab, setTab] = useState("users");

  const [users, setUsers] = useState([]);
  const [apps, setApps] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", full_name: "", role: "user", password: "" });
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [generatedInvite, setGeneratedInvite] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [passwordFor, setPasswordFor] = useState(null);

  const [editingApp, setEditingApp] = useState(null);
  const [bases, setBases] = useState([window.location.origin]);

  const load = useCallback(async () => {
    try {
      const [userList, appList, categoryList, network] = await Promise.all([
        api.admin.users(),
        api.apps.listAll(),
        api.categories.list(),
        api.admin.network().catch(() => null),
      ]);
      setUsers(userList);
      setApps(appList);
      setCategories(categoryList);
      if (network) setBases(reachableBases(network));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const appsVisibleTo = useCallback(
    (email) =>
      apps.filter((app) => {
        if (app.allowed_emails.length > 0) return app.allowed_emails.includes(email?.toLowerCase());
        return !app.admin_only;
      }).length,
    [apps],
  );

  const filteredUsers = useMemo(() => {
    const needle = search.toLowerCase();
    return users.filter(
      (u) => u.email?.toLowerCase().includes(needle) || u.full_name?.toLowerCase().includes(needle),
    );
  }, [users, search]);

  /* --------------------------------------------------------- user actions */

  const sendInvite = async () => {
    setInviteBusy(true);
    setInviteError("");
    try {
      const result = await api.admin.inviteUser(inviteForm);
      setShowInvite(false);

      if (result.invite_token) {
        setGeneratedInvite({
          email: result.user.email,
          token: result.invite_token,
          expiresInDays: result.expires_in_days,
        });
      } else {
        setCredentials({ email: result.user.email, password: inviteForm.password });
      }
      setInviteForm({ email: "", full_name: "", role: "user", password: "" });
      await load();
    } catch (err) {
      setInviteError(err.message);
    } finally {
      setInviteBusy(false);
    }
  };

  const mutateUser = async (id, patch, message) => {
    try {
      await api.admin.updateUser(id, patch);
      await load();
      if (message) toast.success(message);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const removeUser = async (target) => {
    if (!window.confirm(`Remove ${target.email} from App Hub? They'll lose access immediately.`)) return;
    try {
      await api.admin.removeUser(target.id);
      await load();
      toast.success(`${target.email} removed`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const resendInvite = async (target) => {
    try {
      const result = await api.admin.reinvite(target.id);
      setGeneratedInvite({
        email: target.email,
        token: result.invite_token,
        expiresInDays: result.expires_in_days,
      });
    } catch (err) {
      toast.error(err.message);
    }
  };

  /* --------------------------------------------------------- app actions */

  const saveApp = async (form) => {
    try {
      await api.apps.update(editingApp.id, form);
      setEditingApp(null);
      await load();
      toast.success("App updated");
    } catch (err) {
      toast.error(err.message);
      throw err;
    }
  };

  const deleteApp = async (id) => {
    try {
      await api.apps.remove(id);
      await load();
      toast.success("App removed");
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (loading) {
    return <div className="min-h-dvh bg-slate-950 flex items-center justify-center text-white/60">Loading…</div>;
  }

  return (
    <div className="app-surface min-h-dvh text-white p-4 sm:p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/"
            className="text-white/60 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-colors"
            aria-label="Back to apps"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold">Admin</h1>
            <p className="text-white/40 text-sm">People and the apps they can reach</p>
          </div>
          {tab === "users" && (
            <Button onClick={() => setShowInvite(true)} className="ml-auto bg-purple-600 hover:bg-purple-700">
              <UserPlus className="w-4 h-4 mr-2" /> Add person
            </Button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Stat label="People" value={users.length} />
          <Stat label="Apps" value={apps.length} />
          <Stat label="Restricted" value={apps.filter((a) => a.admin_only || a.allowed_emails.length > 0).length} />
        </div>

        <div className="flex gap-1 mb-6 border-b border-white/10">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.id
                  ? "border-purple-500 text-white"
                  : "border-transparent text-white/50 hover:text-white/80"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "users" && (
          <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
            <div className="p-4 border-b border-white/10 flex items-center gap-3">
              <Search className="w-4 h-4 text-white/40" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search people…"
                className="bg-transparent flex-1 text-white placeholder-white/30 focus:outline-none text-sm"
              />
            </div>

            <div className="divide-y divide-white/10">
              {filteredUsers.map((u) => (
                <div key={u.id} className="flex items-center gap-4 p-4 hover:bg-white/5 transition-colors flex-wrap">
                  <div className="w-9 h-9 rounded-full bg-purple-600/30 flex items-center justify-center text-sm font-bold text-purple-200 shrink-0">
                    {(u.full_name || u.email || "?").charAt(0).toUpperCase()}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">
                      {u.full_name || "—"}
                      {u.id === me.id && <span className="text-white/30 font-normal"> (you)</span>}
                    </p>
                    <p className="text-white/40 text-xs truncate">{u.email}</p>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {u.pending_invite && <Pill tone="yellow">Invite pending</Pill>}
                    {u.disabled && <Pill tone="red">Disabled</Pill>}
                    <Pill tone={u.role === "admin" ? "purple" : "neutral"}>
                      {u.role === "admin" ? <Shield className="w-3 h-3" /> : <UserIcon className="w-3 h-3" />}
                      {u.role}
                    </Pill>
                    <span className="text-white/40 text-xs hidden md:block w-14 text-right">
                      {appsVisibleTo(u.email)} apps
                    </span>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() =>
                        mutateUser(
                          u.id,
                          { role: u.role === "admin" ? "user" : "admin" },
                          `${u.email} is now ${u.role === "admin" ? "a user" : "an admin"}`,
                        )
                      }
                      className="text-white/40 hover:text-white text-xs px-2 py-1 rounded hover:bg-white/10 transition-colors"
                      title={u.role === "admin" ? "Make a regular user" : "Make an admin"}
                    >
                      {u.role === "admin" ? "Demote" : "Promote"}
                    </button>

                    <button
                      onClick={() => setPasswordFor(u)}
                      className="text-white/40 hover:text-white p-1.5 rounded hover:bg-white/10 transition-colors"
                      title="Set password"
                    >
                      <KeyRound className="w-3.5 h-3.5" />
                    </button>

                    {u.pending_invite && (
                      <button
                        onClick={() => resendInvite(u)}
                        className="text-white/40 hover:text-white p-1.5 rounded hover:bg-white/10 transition-colors"
                        title="Get a new invite link"
                      >
                        <Link2 className="w-3.5 h-3.5" />
                      </button>
                    )}

                    {u.id !== me.id && (
                      <button
                        onClick={() => removeUser(u)}
                        className="text-white/40 hover:text-red-400 p-1.5 rounded hover:bg-white/10 transition-colors"
                        title="Remove this person"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {filteredUsers.length === 0 && (
                <div className="p-8 text-center text-white/30 text-sm">Nobody matches that search.</div>
              )}
            </div>
          </div>
        )}

        {tab === "apps" && (
          <div className="grid gap-3">
            {apps.map((app) => (
              <button
                key={app.id}
                onClick={() => setEditingApp(app)}
                className="bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 p-4 flex items-center gap-4 text-left transition-colors"
              >
                <div className="w-10 h-10 flex items-center justify-center shrink-0">
                  {app.icon_url ? (
                    <img src={app.icon_url} alt="" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <div className="w-full h-full rounded-xl bg-white/10 flex items-center justify-center text-sm font-semibold text-white/70">
                      {app.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm flex items-center gap-1.5">
                    <Globe className="w-3 h-3 text-white/40" />
                    {app.name}
                  </p>
                  <p className="text-white/40 text-xs truncate">{app.url}</p>
                </div>

                <div className="flex gap-2 flex-wrap justify-end">
                  {app.admin_only ? (
                    <Pill tone="red">Admins only</Pill>
                  ) : app.allowed_emails.length > 0 ? (
                    <Pill tone="yellow">{app.allowed_emails.length} allowed</Pill>
                  ) : (
                    <Pill tone="green">Everyone</Pill>
                  )}
                  {app.category_name && <Pill>{app.category_name}</Pill>}
                </div>
              </button>
            ))}

            {apps.length === 0 && (
              <p className="text-center text-white/30 text-sm py-12">
                No apps yet — add one from the main screen.
              </p>
            )}
          </div>
        )}

      </div>

      {/* Add someone */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add someone</DialogTitle>
            <DialogDescription>
              Set a password and pass it on yourself, or send them a link to choose their own.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="invite-email">Email *</Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="person@goldstarprinting.us"
                autoFocus
              />
            </div>

            <div>
              <Label htmlFor="invite-name">Name</Label>
              <Input
                id="invite-name"
                value={inviteForm.full_name}
                onChange={(e) => setInviteForm((f) => ({ ...f, full_name: e.target.value }))}
                placeholder="Optional"
              />
            </div>

            <div>
              <Label>Role</Label>
              <div className="flex gap-2 mt-1">
                {["user", "admin"].map((role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => setInviteForm((f) => ({ ...f, role }))}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      inviteForm.role === role
                        ? "bg-slate-900 border-slate-900 text-white"
                        : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {role === "admin" ? "Admin" : "User"}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="invite-password" className="mb-0">Password</Label>
                <button
                  type="button"
                  onClick={() => setInviteForm((f) => ({ ...f, password: generatePassword() }))}
                  className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
                >
                  <RefreshCw className="h-3 w-3" /> Generate
                </button>
              </div>
              <Input
                id="invite-password"
                value={inviteForm.password}
                onChange={(e) => setInviteForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="Leave empty to send an invite link instead"
                className="mt-1 font-mono text-sm"
                spellCheck={false}
              />
              <p className="mt-1 text-xs text-gray-500">
                {inviteForm.password
                  ? "You'll get their sign-in details to copy once the account is made."
                  : "With no password, you'll get a one-time link for them to set their own."}
              </p>
            </div>

            {inviteError && <p className="text-red-600 text-sm">{inviteError}</p>}
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowInvite(false)} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={sendInvite}
              disabled={!inviteForm.email || inviteBusy}
              className="flex-1 bg-purple-600 hover:bg-purple-700"
            >
              {inviteBusy ? "Creating…" : "Create account"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <InviteLinkDialog invite={generatedInvite} bases={bases} onClose={() => setGeneratedInvite(null)} />

      <CredentialsDialog
        details={credentials}
        address={bases[0] ?? window.location.origin}
        onClose={() => setCredentials(null)}
      />

      <SetPasswordDialog
        person={passwordFor}
        address={bases[0] ?? window.location.origin}
        onClose={() => setPasswordFor(null)}
        onDone={load}
      />

      <AppFormModal
        open={!!editingApp}
        onClose={() => setEditingApp(null)}
        onSave={saveApp}
        onDelete={deleteApp}
        app={editingApp}
        categories={categories}
      />
    </div>
  );
}
